// One live picture per browser tab, shared by the home page's hero card and
// /live. Clicking from one to the other (or going Back) hands the playing
// WebRTC connection across, so the picture keeps going instead of
// connecting all over again (which takes a few seconds). A connection nobody
// picks up within a few seconds is hung up.
//
// The latest booth state and game travel the same way, so the page you land
// on draws right away instead of flashing "the booth is dark" while it asks.

const HOLD_MS = 12_000;
let held = null; // { whep, session, timer }
const recent = new Map(); // "state" | "game" -> { data, at }

// A page is done with its picture but the next one may want it.
export function holdSession(whep, session) {
  if (!whep || !session) return;
  if (held && held.session !== session) held.session.stop();
  clearTimeout(held?.timer);
  const timer = setTimeout(() => {
    if (held?.session === session) {
      held = null;
      session.stop();
    }
  }, HOLD_MS);
  held = { whep, session, timer };
}

// The held connection, if it's for this stream and still alive.
export function takeSession(whep) {
  const h = held;
  if (!h || h.whep !== whep) return null;
  held = null;
  clearTimeout(h.timer);
  const st = h.session.pc?.connectionState;
  if (st === "failed" || st === "closed") {
    h.session.stop();
    return null;
  }
  return h.session;
}

// Put a taken connection into this page's <video> and report its state the
// way play() would.
export function adoptSession(session, video, onState) {
  video.srcObject = session.stream;
  session.pc.addEventListener("connectionstatechange", () => onState?.(session.pc.connectionState));
  queueMicrotask(() => onState?.(session.pc.connectionState));
  return session;
}

// The picture's watchdog. Each player asks it once a second whether its
// connection is still bringing Greg in, and it says "connect again" only when
// a new connection would actually help.
//
// It reads the connection's own WebRTC numbers (video frames decoded, sound
// packets received), never the <video>'s frame counter. Safari's
// getVideoPlaybackQuality() can sit still on a playing stream: on Oct 14 a
// Mac viewer's watchdog trusted it and hung up on a healthy picture every
// 4 seconds, the new connection took ~3 s to show anything, and they saw
// black with a second of Greg now and then (Greg's end was fine all night).
//
// - Nothing at all arriving (no frames, no sound) for DEAD_MS, or FIRST_MS
//   on a brand-new connection: it's dead, connect again.
// - Sound arriving but no picture: Greg's end paused his video (a Wi-Fi or
//   camera hiccup, see sendHealth.js). A new connection can't fix that, and
//   every viewer reconnecting at once asks his upload for a fresh keyframe
//   each, so wait PICTURE_MS before trying.
// - Picture arriving but no sound for SOUND_MS ("sound", only on /live, while
//   the viewer has the sound on and once his sound has come through here):
//   it got lost on its way to this viewer. On Oct 18 viewers in Toronto lost
//   it mid-broadcast while Cloudflare was taking in Greg's sound at a steady
//   50 packets a second and everyone else heard him; the old watchdog never
//   noticed a picture with no sound, and one viewer reloaded six times before
//   giving up. His microphone turned off on purpose still sends silence,
//   packet after packet, so it never counts. The player tries a new
//   connection beside this one and keeps the picture up meanwhile (live.jsx).
// - Reconnects in a row wait longer and longer (CALM_MS), so nothing can
//   loop again; a minute of steady picture (and sound) forgets them.
const FIRST_MS = 10_000;
const DEAD_MS = 5_000;
const PICTURE_MS = 20_000;
const SOUND_MS = 6_000;
const CALM_MS = [0, 15_000, 30_000, 60_000];
const STEADY_MS = 60_000;

// What this connection has taken in so far, or null if the browser won't say
// (then the watchdog doesn't guess).
async function received(pc) {
  const n = { frames: 0, video: 0, sound: 0 };
  try {
    (await pc.getStats()).forEach((s) => {
      if (s.type !== "inbound-rtp") return;
      if (s.kind === "video") {
        n.frames = s.framesDecoded ?? s.framesReceived ?? 0;
        n.video = s.packetsReceived ?? 0;
      }
      if (s.kind === "audio") n.sound = s.packetsReceived ?? 0;
    });
  } catch {
    return null;
  }
  return n;
}

// sound: also watch that Greg's sound keeps coming (/live; the home page's
// card plays muted, so it only cares about the picture).
export function flowWatch({ sound: forSound = false } = {}) {
  let pc = null;
  let frames = 0;
  let video = 0;
  let sound = 0;
  let mediaAt = 0; // last time anything new arrived (or the connection appeared)
  let frameAt = 0; // last time a new frame arrived
  let videoAt = 0; // last time anything of the picture arrived
  let soundAt = 0; // last time new sound arrived (or the connection appeared)
  let anyMedia = false;
  let heard = false; // his sound has come through on a connection here
  let steadySince = 0;
  let tries = 0;
  let triedAt = 0;
  return {
    // Start over (page hidden, paused): stale numbers mustn't count against
    // the connection when the viewer is back.
    reset() {
      pc = null;
    },
    // Why it's time to connect again ("dead": nothing arriving; "picture":
    // sound but no picture; "sound": picture but no sound), or false.
    // listening: the viewer has the sound on (a muted player doesn't need it).
    // seen: the picture is on screen. Scrolled away (typing a question while
    // listening, say) only the sound is judged: a browser may stop drawing a
    // picture nobody can see, so it isn't held against the connection.
    async check(session, { listening = false, seen = true } = {}) {
      const p = session?.pc;
      if (!p || p.connectionState === "closed") return false;
      if (p !== pc) {
        pc = p;
        frames = 0;
        video = 0;
        sound = 0;
        mediaAt = Date.now();
        frameAt = mediaAt;
        videoAt = mediaAt;
        soundAt = mediaAt;
        anyMedia = false;
        steadySince = 0;
      }
      const n = await received(p);
      if (!n || p !== pc) return false;
      const now = Date.now();
      const newFrames = n.frames > frames;
      const newVideo = n.video > video;
      const newSound = n.sound > sound;
      frames = Math.max(frames, n.frames);
      video = Math.max(video, n.video);
      sound = Math.max(sound, n.sound);
      if (newSound) {
        soundAt = now;
        heard = true;
      }
      const wantSound = forSound && listening && heard;
      if (newFrames) frameAt = now;
      if (newFrames || newVideo) videoAt = now;
      if (newFrames && (newSound || !wantSound)) {
        if (!steadySince) steadySince = now;
        if (now - steadySince >= STEADY_MS) tries = 0;
      } else steadySince = 0;
      if (newFrames || newSound) {
        mediaAt = now;
        anyMedia = true;
      }
      if (!seen) {
        frameAt = now;
        mediaAt = now;
      }
      const dead = now - mediaAt >= (anyMedia ? DEAD_MS : FIRST_MS);
      const blind = now - frameAt >= PICTURE_MS;
      // His picture is coming right now: it's only the sound.
      const deaf = wantSound && now - soundAt >= SOUND_MS && now - videoAt < 2000;
      if (!dead && !blind && !deaf) return false;
      if (now - triedAt < CALM_MS[Math.min(tries, CALM_MS.length - 1)]) return false;
      tries += 1;
      triedAt = now;
      pc = null;
      return dead ? "dead" : blind ? "picture" : "sound";
    },
  };
}

// A new connection tried beside one that lost Greg's sound: true once his
// sound comes through on it (with his picture, or a few seconds on), false
// if it doesn't within ms (then the old one stays).
export async function bringsSound(pc, ms = 8000) {
  const t0 = Date.now();
  let heardAt = 0;
  while (Date.now() - t0 < ms) {
    if (pc.connectionState === "failed" || pc.connectionState === "closed") return false;
    const n = await received(pc);
    if (n?.sound > 0) {
      if (n.frames > 0) return true;
      heardAt ||= Date.now();
      if (Date.now() - heardAt >= 3000) return true;
    }
    await new Promise((done) => setTimeout(done, 250));
  }
  return false;
}

// How the picture has done since /live's last presence ping: how many times
// it connected again, and why. On Oct 14 a viewer's picture reconnected every
// 4 seconds and only their phone video showed it; now each ping carries this
// and the Worker logs it (functions/api/booth.js) for `wrangler tail`. The
// home card counts here too: the home page has no ping of its own, so its
// reconnects ride along with /live's next one.
let tally = { rc: 0, why: {} };

// A player is connecting again: "dead", "picture" or "sound" (flowWatch),
// "failed" or "disconnected" (the connection's state), "ended" (its track),
// "back" (the viewer came back to a dropped one), "error" (connecting threw).
export function noteReconnect(why) {
  tally.rc += 1;
  tally.why[why] = (tally.why[why] || 0) + 1;
}

// The counts for this ping (null if there were none), starting over.
export function takePlayback() {
  if (!tally.rc) return null;
  const pb = { ...tally, b: browser() };
  tally = { rc: 0, why: {} };
  return pb;
}

// Counts whose ping never got there go out with the next one.
export function keepPlayback(pb) {
  if (!pb) return;
  tally.rc += pb.rc;
  for (const [why, n] of Object.entries(pb.why)) tally.why[why] = (tally.why[why] || 0) + n;
}

// The browser's family and nothing finer. On an iPhone or iPad every browser
// is Safari underneath, and the engine is what matters for playback.
function browser() {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return "safari";
  if (/Edg(A|iOS)?\//.test(ua)) return "edge";
  if (/Firefox\/|FxiOS\//.test(ua)) return "firefox";
  if (/Chrome\/|CriOS\//.test(ua)) return "chrome";
  if (/Safari\//.test(ua)) return "safari";
  return "other";
}

export function remember(kind, data) {
  if (data) recent.set(kind, { data, at: Date.now() });
}

// What the last page knew, if it's fresh enough to draw with.
export function recall(kind, maxAge = 30_000) {
  const r = recent.get(kind);
  return r && Date.now() - r.at < maxAge ? r.data : null;
}

// Clicking in is a tap, which lets /live start with the sound on.
let soundAt = 0;
export const soundWanted = () => Date.now() - soundAt < 8000;

// Open /live (or the night's page, while one of the winter's tapes is on)
// without a page load: the app's router listens for popstate.
export function goLive(e, path = "/live") {
  if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0)) return;
  e?.preventDefault();
  soundAt = Date.now();
  window.history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo(0, 0);
}
